import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { getAnamnesisApiErrorMessage, patientAnamnesisApi } from '@/api/patient-anamnesis';
import { Trash, ExportCurve, Notepad2 } from '@/components/icons';
import { ReportData } from '@/components/preview/types';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { AppDialogLayout, BackgroundWorkNotice } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import AnamnesisOverrideConfirmAlert from '@/components/wizard/AnamnesisOverrideConfirmAlert';
import AnamnesisPasteView from '@/components/wizard/AnamnesisPasteView';
import AnamnesisSourceChoice from '@/components/wizard/AnamnesisSourceChoice';
import { useClinicalHistoryStatus } from '@/hooks/use-clinical-history-status';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import {
    getPatientAnamnesisQueryKey,
    usePatientAnamnesis,
    useSaveAnamnesis,
} from '@/hooks/use-patient-anamnesis';
import { formatRelativeToNow } from '@/lib/date';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { describeAnamnesis } from '@/pages/patients/anamnesis/utils';

interface AnamnesisUploadModalProps {
    data: ReportData;
    onUpdate: (data: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>)) => void;
}

const formatRelative = (value?: string) => formatRelativeToNow(value, '');

export default function AnamnesisUploadModal({ data, onUpdate }: AnamnesisUploadModalProps) {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    // The modal has two views: the summary/upload one, and the editor where a
    // ready anamnesis is pasted in. Swapping in place keeps this component — and
    // therefore the clinical-history poll below — mounted.
    const [view, setView] = useState<'manage' | 'paste'>('manage');
    const [pasteError, setPasteError] = useState<string | null>(null);
    const confirmDelete = useConfirmDelete();
    const queryClient = useQueryClient();
    const anamnesisAccess = useFeatureEntitlement('anamnesis.process');

    const patientId = data.patient?.id;
    const { data: anamnesis } = usePatientAnamnesis(patientId);
    const saveInline = useSaveAnamnesis(patientId);
    const hasInlineAnamnesis = anamnesis?.source === 'inline';
    const hasAnamnesis = Boolean(anamnesis);

    const { data: clinicalHistoryResponse, refetch: refetchClinicalHistoryStatus } =
        useClinicalHistoryStatus(data.id, {
            enabled: Boolean(data.id),
        });

    const isBlocked = !data.id || !patientId;
    const clinicalHistory = clinicalHistoryResponse?.data.clinical_history;
    const clinicalHistoryStatus = clinicalHistory?.status ?? 'not_started';
    const clinicalHistoryReady = Boolean(clinicalHistory?.ready);
    // Still being prepared: the modal tells the user they may close it and come back.
    const isClinicalHistoryPending =
        !clinicalHistoryReady &&
        (clinicalHistoryStatus === 'queued' || clinicalHistoryStatus === 'building');

    const handleFileUpload = async (files: File[]) => {
        const file = files[0];
        if (!file || !patientId) return;

        if (anamnesisAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(anamnesisAccess.decision));
            return;
        }

        const validTypes = ['.docx', '.txt', '.pdf'];
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

        if (!fileExtension || !validTypes.includes(fileExtension)) {
            toast.error('Por favor, envie um arquivo .docx, .txt ou .pdf');
            return;
        }

        setUploadError(null);
        setUploading(true);
        try {
            const saved = await patientAnamnesisApi.uploadFile(patientId, file);
            queryClient.setQueryData(getPatientAnamnesisQueryKey(patientId), saved);
            onUpdate(() => ({ has_anamnesis: true }));
            await refetchClinicalHistoryStatus();
            toast.success('Arquivo enviado com sucesso');
        } catch (error) {
            setUploadError(
                getAnamnesisApiErrorMessage(error) ?? 'Falha ao enviar arquivo. Tente novamente.',
            );
            toast.error('Falha ao enviar arquivo');
        } finally {
            setUploading(false);
        }
    };

    const requestUpload = (files: File[]) => {
        const file = files[0];
        if (!file) return;

        // Uploading replaces the anamnesis typed in the patient profile — that is
        // real written work, so ask first. Replacing a previous file is not.
        if (hasInlineAnamnesis) {
            setPendingFile(file);
            return;
        }

        void handleFileUpload([file]);
    };

    const handleSavePaste = async (html: string) => {
        if (!patientId) return;

        setPasteError(null);
        try {
            await saveInline.mutateAsync({ content_html: html });
            onUpdate(() => ({ has_anamnesis: true }));
            await refetchClinicalHistoryStatus();
            toast.success('Anamnese salva');
            setView('manage');
        } catch (error) {
            // Actionable rejections (content too long, plan limit) carry the
            // server's own pt-BR message, and the editor keeps what was typed.
            setPasteError(
                getAnamnesisApiErrorMessage(error) ??
                    'Não foi possível salvar a anamnese. Tente novamente.',
            );
        }
    };

    const handleRemove = () => {
        confirmDelete({
            title: 'Remover anamnese?',
            description:
                'Tem certeza que deseja remover o arquivo de anamnese do paciente? A geração da demanda e história clínica pode ficar indisponível até um novo envio.',
            confirmText: 'Remover',
            onConfirm: async (close) => {
                if (!patientId) return;
                try {
                    await patientAnamnesisApi.remove(patientId);
                    queryClient.invalidateQueries({
                        queryKey: getPatientAnamnesisQueryKey(patientId),
                    });
                    onUpdate(() => ({ has_anamnesis: false }));
                    setUploadError(null);
                    await refetchClinicalHistoryStatus();
                    toast.success('Arquivo removido');
                    close();
                } catch {
                    toast.error('Erro ao remover arquivo');
                }
            },
        });
    };

    // The modal owns its chrome (the callers pass no title) so the paste view
    // can seat its back control in the header row, beside the title and the
    // close, instead of stranding it in the footer.
    const shell = (children: ReactNode, backAction?: { label: string; onClick: () => void }) => (
        <AppDialogLayout title="Gerenciar anamnese" backAction={backAction}>
            {children}
        </AppDialogLayout>
    );

    if (isBlocked) {
        return shell(
            <div className="rounded-lg border border-border bg-surface p-4">
                <p className="text-sm font-medium text-foreground">
                    Selecione um paciente e salve o laudo para enviar a anamnese.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                    Assim que esses dados estiverem disponíveis, o envio será liberado.
                </p>
            </div>,
        );
    }

    if (view === 'paste') {
        const goBack = () => {
            setPasteError(null);
            setView('manage');
        };

        return shell(
            <AnamnesisPasteView
                // The existing document — including text extracted from a file —
                // so the editor opens on real content instead of a blank page.
                initialHtml={anamnesis?.content_html ?? ''}
                saving={saveInline.isPending}
                error={pasteError}
                onSave={(html) => void handleSavePaste(html)}
            />,
            { label: 'Voltar', onClick: goBack },
        );
    }

    return shell(
        <div className="space-y-4">
            {anamnesisAccess.shouldBlock && (
                <Alert variant="warning" className="text-xs">
                    <div className="flex items-center justify-between gap-3">
                        <span>{getEntitlementBlockedMessage(anamnesisAccess.decision)}</span>
                        {anamnesisAccess.isUnavailableInPlan &&
                        anamnesisAccess.availableFromPlan ? (
                            <FeatureUnavailableChip
                                availablePlan={anamnesisAccess.availableFromPlan}
                            />
                        ) : null}
                    </div>
                </Alert>
            )}

            {pendingFile && (
                <AnamnesisOverrideConfirmAlert
                    onCancel={() => setPendingFile(null)}
                    onConfirm={() => {
                        const file = pendingFile;
                        setPendingFile(null);
                        void handleFileUpload([file]);
                    }}
                />
            )}

            {!hasAnamnesis && (
                <AnamnesisSourceChoice
                    uploading={uploading}
                    disabled={anamnesisAccess.shouldBlock}
                    onFilesSelected={requestUpload}
                    onWrite={() => setView('paste')}
                />
            )}

            {uploadError && <Alert variant="destructive">{uploadError}</Alert>}

            {anamnesis && (
                <div className="space-y-4">
                    <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-4">
                        <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-brand-subtle text-brand">
                            {anamnesis.source === 'uploaded' ? (
                                <ExportCurve className="size-4" />
                            ) : (
                                <Notepad2 className="size-4" />
                            )}
                        </span>
                        <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">
                                {anamnesis.source === 'uploaded'
                                    ? (anamnesis.file_name ?? 'Arquivo enviado')
                                    : 'Anamnese escrita no LaudoPsi'}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {anamnesis.source === 'uploaded'
                                    ? `Enviado ${formatRelative(anamnesis.updated_at) ?? ''}`
                                    : describeAnamnesis(anamnesis)}
                            </p>
                        </div>
                    </div>

                    {isClinicalHistoryPending && (
                        <BackgroundWorkNotice description="A anamnese já está salva. A IA ainda está lendo o texto para usá-lo nas seções do laudo, e isso continua em segundo plano." />
                    )}

                    <div className="flex items-center justify-end gap-2">
                        <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    {/* Default size across the row: it is what
                                        the dialog's own back and close controls
                                        are, so the modal has one button height
                                        from the header down. */}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setView('paste')}
                                        disabled={uploading}
                                    >
                                        <Notepad2 />
                                        Editar texto
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {anamnesis.source === 'uploaded'
                                        ? 'Colar ou corrigir o texto extraído do arquivo'
                                        : 'Colar ou editar a anamnese em texto'}
                                </TooltipContent>
                            </Tooltip>

                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <label
                                        className={`inline-flex ${
                                            uploading || anamnesisAccess.shouldBlock
                                                ? 'cursor-not-allowed'
                                                : 'cursor-pointer'
                                        }`}
                                    >
                                        <input
                                            type="file"
                                            accept=".docx,.txt,.pdf"
                                            onChange={(event) => {
                                                requestUpload(
                                                    Array.from(event.currentTarget.files ?? []),
                                                );
                                                event.currentTarget.value = '';
                                            }}
                                            className="hidden"
                                            disabled={uploading || anamnesisAccess.shouldBlock}
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            asChild
                                            disabled={uploading || anamnesisAccess.shouldBlock}
                                        >
                                            <span className="cursor-inherit">
                                                <ExportCurve />
                                                {uploading
                                                    ? 'Enviando...'
                                                    : 'Substituir por arquivo'}
                                            </span>
                                        </Button>
                                    </label>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {hasInlineAnamnesis
                                        ? 'Enviar um arquivo no lugar da anamnese escrita'
                                        : 'Substituir arquivo'}
                                </TooltipContent>
                            </Tooltip>

                            {anamnesis.source === 'uploaded' && (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    // `icon` is the square that matches the
                                    // default height the two buttons beside it
                                    // and the header controls all use.
                                    size="icon"
                                    onClick={handleRemove}
                                    disabled={uploading}
                                    aria-label="Remover anamnese"
                                >
                                    <Trash />
                                </Button>
                            )}
                        </TooltipProvider>
                    </div>
                </div>
            )}
        </div>,
    );
}
